import axiosInstance from '../../../config/axios';
import { SupplierOrder, OrderItem, ReceiveItem, CatalogItem, GeneratedOrders } from '../types';

/** Service for supplier order management, item handling, status transitions, and catalog access. */
class OrderService {
  private static readonly BASE = '/api/admin/orders';

  static async getOrders(params?: {
    work_area?: number;
    status?: string;
    supplier?: number;
  }): Promise<SupplierOrder[]> {
    const response = await axiosInstance.get(`${this.BASE}/`, { params });
    return response.data;
  }

  static async createOrder(data: {
    work_area_id: number;
    supplier_id: number;
    notes?: string;
  }): Promise<{ id: number; status: string }> {
    const response = await axiosInstance.post(`${this.BASE}/`, data);
    return response.data;
  }

  static async getOrderDetail(orderId: number): Promise<SupplierOrder> {
    const response = await axiosInstance.get(`${this.BASE}/${orderId}/`);
    return response.data;
  }

  static async updateOrder(
    orderId: number,
    data: { notes?: string; expected_delivery?: string }
  ): Promise<SupplierOrder> {
    const response = await axiosInstance.patch(`${this.BASE}/${orderId}/`, data);
    return response.data;
  }

  static async deleteOrder(orderId: number): Promise<void> {
    await axiosInstance.delete(`${this.BASE}/${orderId}/`);
  }

  static async addItem(
    orderId: number,
    data: { inventory_item_id: number; quantity: number }
  ): Promise<OrderItem> {
    const response = await axiosInstance.post(`${this.BASE}/${orderId}/items/`, data);
    return response.data;
  }

  static async updateItem(
    orderId: number,
    itemId: number,
    data: { quantity_ordered: number }
  ): Promise<OrderItem> {
    const response = await axiosInstance.patch(
      `${this.BASE}/${orderId}/items/${itemId}/`,
      data
    );
    return response.data;
  }

  static async removeItem(orderId: number, itemId: number): Promise<void> {
    await axiosInstance.delete(`${this.BASE}/${orderId}/items/${itemId}/`);
  }

  static async sendOrder(
    orderId: number,
    data?: { expected_delivery?: string }
  ): Promise<SupplierOrder> {
    const response = await axiosInstance.post(`${this.BASE}/${orderId}/send/`, data);
    return response.data;
  }

  static async markDelivered(orderId: number): Promise<SupplierOrder> {
    const response = await axiosInstance.post(`${this.BASE}/${orderId}/mark-delivered/`);
    return response.data;
  }

  static async receiveOrder(
    orderId: number,
    items: ReceiveItem[]
  ): Promise<SupplierOrder> {
    const response = await axiosInstance.post(`${this.BASE}/${orderId}/receive/`, {
      items,
    });
    return response.data;
  }

  static async cancelOrder(orderId: number): Promise<SupplierOrder> {
    const response = await axiosInstance.post(`${this.BASE}/${orderId}/cancel/`);
    return response.data;
  }

  static async getCatalog(
    supplierId: number,
    params?: { exclude_order?: number; search?: string }
  ): Promise<CatalogItem[]> {
    const response = await axiosInstance.get(
      `${this.BASE}/catalog/${supplierId}/`,
      { params }
    );
    return response.data;
  }

  static async generateFromSession(
    sessionId: number,
    workAreaId: number
  ): Promise<GeneratedOrders> {
    const response = await axiosInstance.post(
      `${this.BASE}/generate-from-session/${sessionId}/`,
      { work_area_id: workAreaId }
    );
    return response.data;
  }
}

export default OrderService;
